import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, TextField, Button, 
    CircularProgress, Typography, 
    FormControlLabel, Checkbox, 
    IconButton, 
    List, 
    ListItem, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel 
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const initialValues = {   
    acTypeCode: '',
    acRegs: [],
    sectors: [],
    crewComplement: [{ rankId: '', quantity: 1, isCompulsory: false }],
    isActive: true,
};

const CrewComplementPage = () => {
    useAuthCheck();
    const { crewComplementId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [acTypes, setACTypes] = useState([]);
    const [aircrafts, setAircrafts] = useState([]);
    const [filteredAircrafts, setFilteredAircrafts] = useState([]);
    const [airportPairs, setAirportPairs] = useState([]);

    const ranks = useSelector((state) => state.crewRanks);
    const [formValues, setFormValues] = useState(initialValues);


    const fetchData = useCallback(async (url, setter, errorMsg) => {
        try {
            const { data } = await api.get(url);
            setter(data);
        } catch (err) {
            setError(err);
            toast.error(err.response?.data?.message || errorMsg);
        }
    }, []);         

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const urls = [
                    { url: '/actypes/active', setter: setACTypes, errorMsg: 'Failed to fetch ACTypes!' },
                    { url: '/aircrafts/active', setter: setAircrafts, errorMsg: 'Failed to fetch aircrafts!' },
                    { url: '/airportpairs/unique', setter: setAirportPairs, errorMsg: 'Failed to fetch airport pairs!' },
                ];
                if (crewComplementId) {
                    urls.push({ 
                        url: `/crewcomplements/${crewComplementId}`,
                        setter: (data) => setFormValues({ ...initialValues, ...data }),
                        errorMsg: 'Failed to fetch crew complement!',
                    });
                }
        
                await Promise.all(urls.map(({ url, setter, errorMsg }) => fetchData(url, setter, errorMsg)));
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };  

        fetchAllData();
    }, [fetchData, crewComplementId]);
    
    useEffect(() => {
        if (formValues.acTypeCode) {
            const filtered = aircrafts.filter(ac => ac.acTypeCode === formValues.acTypeCode);
            setFilteredAircrafts(filtered);
        } else {
            setFilteredAircrafts([]); 
        }
    }, [formValues.acTypeCode, aircrafts]);

    const checkoutSchema = yup.object().shape({
        acTypeCode: yup.string().required('Required'),        
        // crewComplement: yup.array().of(
        //     yup.object().shape({
        //         rankId: yup.string().required('Rank ID is required').oneOf(ranks.map(rank => rank._id), 'Invalid rank'),
        //         quantity: yup.number().required('Quantity is required').min(0),
        //         isCompulsory: yup.boolean().required('Is Compulsory is required'),
        //     })
        // )        
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = crewComplementId ? "PUT" : "POST";
            const url = crewComplementId
                ? `/crewcomplements/update/${crewComplementId}` 
                : '/crewcomplements/new'

            const res = await api({
                method,
                url,
                data: values,
            });

            if (![200, 201].includes(res.status)) {
                toast.error("Failed to submit the form!");
                return;
            }

            toast.success(crewComplementId 
                ? 'Crew complement updated successfully.' 
                : 'Crew complement created successfully.'
            );

            if (!crewComplementId) {
                onSubmitProps.resetForm();
            }

            navigate("/crewcomplements");
        } catch (error) {
            setError(error);
            toast.error(error?.response?.data?.message || "Failed to save crew complement!");
        }
    };

    const addCrewMember = (values, setFieldValue) => {
        setFieldValue('crewComplement', [...values.crewComplement, { rankId: '', quantity: 1, isCompulsory: false }]);
    };
    
    const removeCrewMember = (values, setFieldValue, index) => {
        const newCrewRank = values.crewComplement.filter((_, i) => i !== index);
        setFieldValue('crewComplement', newCrewRank);
    };
    

    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Crew Complement"
                    subtitle={`${loading ? 'Loading...' : crewComplementId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/crewcomplements")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >
                                    <FormControl required variant="filled" sx={{ gridColumn: "span 4" }} >
                                        <InputLabel>AC Type</InputLabel>
                                        <Select
                                            label="AC Type"
                                            name="acTypeCode"                                            
                                            value={values.acTypeCode || ""}
                                            onChange={(e) => {
                                                setFieldValue("acTypeCode", e.target.value);
                                                setFormValues({ ...initialValues, acTypeCode: e.target.value })
                                            }}
                                        >
                                            {acTypes.map((ac, index) => (
                                                <MenuItem 
                                                    key={index} 
                                                    value={ac.acTypeCode}
                                                >
                                                    {ac.acTypeCode}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl variant="filled" sx={{ gridColumn: "span 4" }} >
                                        <InputLabel>AC Registrations</InputLabel>
                                        <Select                                            
                                            multiple
                                            label="AC Registrations"
                                            name="acRegs"                                            
                                            value={values.acRegs}
                                            onChange={(e) => setFieldValue("acRegs", e.target.value)}
                                            renderValue={(selected) => selected.join(", ")}
                                        >
                                            {filteredAircrafts.map((ac, index) => (
                                                <MenuItem 
                                                    key={index} 
                                                    value={ac.acReg}
                                                >
                                                    {ac.acReg}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    
                                    <FormControl required variant="filled" sx={{ gridColumn: "span 4" }} >
                                        <InputLabel>Sectors</InputLabel>
                                        <Select
                                            multiple
                                            label="Sectors"
                                            name="sectors"                                            
                                            value={values.sectors}
                                            onChange={(e) => setFieldValue("sectors", e.target.value)}
                                            renderValue={(selected) => selected.join(", ")}
                                        >
                                            {airportPairs.map((ap, index) => (
                                                <MenuItem 
                                                    key={index} 
                                                    value={ap.sector}
                                                >
                                                    {ap.sector}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Typography variant="h6" fontWeight="bold" sx={{ gridColumn: 'span 12' }}>
                                        Crew Complement
                                    </Typography>

                                    <List sx={{ gridColumn: 'span 12' }}>
                                        {values.crewComplement.map((crew, index) => (
                                            <ListItem key={index} secondaryAction={
                                                <IconButton edge="end" aria-label="delete" onClick={() => removeCrewMember(values, setFieldValue, index)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            }>
                                                <Box display="flex" gap={1} width="100%">
                                                    <FormControl fullWidth variant="filled" sx={{ minWidth: isMobile ? '30%' : '40%' }}>
                                                        <InputLabel id={`rank-label-${index}`}>Rank {index + 1}</InputLabel>
                                                        <Select
                                                            labelId={`rank-label-${index}`}
                                                            label={`Rank ID ${index + 1}`}
                                                            id={`rank-select-${index}`}
                                                            name={`crewComplement[${index}].rankId`}
                                                            value={crew.rankId}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={touched.crewComplement?.[index]?.rankId && !!errors.crewComplement?.[index]?.rankId}
                                                        >
                                                            {ranks.map((rank) => (
                                                                <MenuItem key={rank._id} value={rank._id}>
                                                                    {rank.rankCode} 
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {touched.crewComplement?.[index]?.rankId && errors.crewComplement?.[index]?.rankId && (
                                                            <Typography color="error" variant="caption">
                                                                {errors.crewComplement[index].rankId}
                                                            </Typography>
                                                        )}
                                                    </FormControl>

                                                    <TextField
                                                        fullWidth
                                                        variant="filled"
                                                        type="number"
                                                        label={`Quantity ${index + 1}`}
                                                        name={`crewComplement[${index}].quantity`}
                                                        value={crew.quantity}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.crewComplement?.[index]?.quantity && !!errors.crewComplement?.[index]?.quantity}
                                                        helperText={touched.crewComplement?.[index]?.quantity && errors.crewComplement?.[index]?.quantity}
                                                        sx={{ minWidth: isMobile ? '30%' : '40%' }}
                                                    />

                                                    <FormControlLabel
                                                        sx={{ minWidth: isMobile ? '30%' : '20%' }}
                                                        control={
                                                            <Checkbox
                                                                checked={crew.isCompulsory} // Bind to specific crew item's isCompulsory
                                                                onChange={handleChange}
                                                                name={`crewComplement[${index}].isCompulsory`}
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Is Compulsory"
                                                    />
                                                </Box>
                                            </ListItem>
                                        ))}
                                        <ListItem>
                                            <Button 
                                                startIcon={<AddIcon />}
                                                onClick={() => addCrewMember(values, setFieldValue)}
                                            >
                                                Add Crew Member
                                            </Button>
                                        </ListItem>
                                    </List>

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {crewComplementId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default CrewComplementPage;